/**
 * Header help menu — a compact "?" icon button in the app header that opens
 * a dropdown with quick links into the Field Guide.
 *
 * Lives in the global header, not on the map.  That keeps the map surface
 * free of yet another floating element (annotations toolbar, AI helper, and
 * back-chip already crowd it on mobile) and makes the help entry reachable
 * from every page, not just /it/map.
 *
 * Mobile UX: the button collapses to a 36 px square that sits next to the
 * language switcher.  The dropdown anchors to the button's right edge with
 * a width clamped against the viewport so it never overflows on a 360 px
 * phone.  Closes on outside-click and on Escape.
 *
 * Desktop UX: same component, same code path — keeps behaviour consistent
 * and the maintenance cost low.
 */

import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  buildGuideUrl,
  isLangCode,
  DEFAULT_LANG,
  type ArticleSlug,
  type LangCode,
  type SectionSlug,
} from '@/features/guide';
import { emit } from '@/lib/events';

interface QuickLink {
  section: SectionSlug;
  article: ArticleSlug;
}

/**
 * The five quick-access targets.  Curated to cover the most-asked surfaces
 * (onboarding, then the four reference articles a first-time user hits).
 * Keep this list under six entries — the dropdown loses scannability past
 * that, especially on mobile where vertical space is precious.
 */
const QUICK_LINKS: readonly QuickLink[] = [
  { section: 'getting-started', article: 'first-five-minutes' },
  { section: 'reference',       article: 'filters-panel' },
  { section: 'reference',       article: 'pins' },
  { section: 'reference',       article: 'shortlist' },
  { section: 'reference',       article: 'compare-table' },
];

export function HeaderHelpMenu({ lang: langProp }: { lang?: LangCode } = {}) {
  const { t, i18n } = useTranslation();
  const lang: LangCode = langProp ?? (isLangCode(i18n.language) ? i18n.language : DEFAULT_LANG);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent | TouchEvent) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const guideLabel = t('guide.label', { defaultValue: 'Field Guide' });
  const buttonAria = t('guide.helpButton.aria', {
    defaultValue: 'Open the {{label}}',
    label: guideLabel,
  });

  const handleOpen = () => {
    if (!open) emit('guide:help-opened', { surface: 'header' });
    setOpen((v) => !v);
  };

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={handleOpen}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={buttonAria}
        title={guideLabel}
        className="inline-flex h-9 min-w-9 items-center justify-center rounded-md border border-[color:var(--color-ink-100)] px-1.5 text-[color:var(--color-ink-700)] hover:bg-[color:var(--color-paper-2)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--color-terra-600)]"
      >
        <span aria-hidden="true" className="font-mono text-sm font-semibold">?</span>
      </button>

      {open && (
        <div
          role="menu"
          aria-label={guideLabel}
          // Anchor to the button's right edge so the dropdown stays inside
          // the viewport on narrow screens; clamp width with min() so it
          // never extends past the screen edge on a ~360 px phone.
          className="absolute right-0 top-[calc(100%+0.375rem)] z-[1090] w-[min(18rem,calc(100vw-1.5rem))] overflow-hidden rounded-lg border border-[color:var(--color-ink-100)] bg-[color:var(--color-paper)] shadow-xl"
        >
          <div className="border-b border-[color:var(--color-ink-100)] px-3 py-2">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[color:var(--color-ink-500)]">
              {t('guide.helpButton.title', { defaultValue: 'How to use the map' })}
            </p>
          </div>
          <ul className="py-1">
            {QUICK_LINKS.map((q) => (
              <li key={q.article}>
                <Link
                  to={buildGuideUrl({ lang, section: q.section, article: q.article })}
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    emit('guide:help-link-clicked', { article: q.article });
                  }}
                  className="block px-3 py-2 text-sm text-[color:var(--color-ink-800)] hover:bg-[color:var(--color-paper-2)] focus:bg-[color:var(--color-paper-2)] focus:outline-none"
                >
                  {t(`guide.article.${q.article}.title`, { defaultValue: q.article })}
                </Link>
              </li>
            ))}
          </ul>
          <div className="border-t border-[color:var(--color-ink-100)] px-3 py-2">
            <Link
              to={buildGuideUrl({ lang })}
              onClick={() => {
                setOpen(false);
                emit('guide:help-link-clicked', { article: 'root' as ArticleSlug });
              }}
              className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-wider text-[color:var(--color-terra-700)] hover:text-[color:var(--color-terra-800)]"
            >
              {t('guide.helpButton.openAll', { defaultValue: 'Open full guide →' })}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
